import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Alert,
} from '@mui/material';
import DialogComponent from '../../component/dialog/dialog.component';
import apiManage from '../../request';
import AiAnalysisCard from './ai-analysis-card.component';
import { DeepPartial } from '../../models/utility-types';
import { parseOpenAIResponse } from '../../constants/tool-open-ai';
import { HighRiskCase, ThresholdRecommendations } from '../../request/api/risk';
import RenderTrend from './ai-analysis-render-trend';
import { mapErrorToI18nKey } from '../../constants/tool-tunnel-config';
import i18n from '../../i18n';
import setGlobalLoading from '../../component/linear-determinate';
import { CustomerLoading } from '../../component/loading/loading';

/**
 * 风控 AI 智能分析对话框组件
 *
 * 功能：
 * - 根据餐厅 ID（stringId）拉取风控分析数据
 * - 展示今日概况、趋势、AI 总结、风险告警、阈值建议五大模块
 * - 支持错误提示与空状态展示
 */
interface Props {
    /** 控制对话框是否打开 */
    open: boolean;
    /** 关闭对话框的回调函数 */
    onClose: () => void;
    /** 餐厅唯一标识（用于请求数据） */
    stringId: string;
}

/**
 * AI 分析结果的数据结构（部分字段可选，适配后端可能缺失的情况）
 */
export type AIAnalysis = DeepPartial<{
    /** 今日风控情况 */
    today_situation: {
        /** 今日触发的风控异常总数 */
        total_orders: number;
        /** 相比昨日的变化量（正为增加，负为减少） */
        yesterdayCnt: number;
        /** 主要异常类型列表（去重合并后的 rule_types + event_types） */
        main_issues: string[];
    };
    /** 风控趋势数据 */
    trend: {
        /** 本周累计异常数 */
        weekly_total: number;
        /** 与上周相比的增长率（百分比，可正可负） */
        weekly_growth_rate: number;
        /** 异常高发时间段（如 ["11:00-13:00", "18:00-20:00"]） */
        peak_time_ranges: string[];
        /** 各异常类型的统计分布 */
        all_type_stats: Array<{
            type: string;     // 异常类型名称
            count: number;    // 触发次数
            percent: number;  // 占比（%）
        }>;
    };
    /** AI 生成的自然语言总结文本 */
    ai_summary: string;
    /** 高风险异常列表（risk_level === 'HIGH'） */
    high_risk_alerts: HighRiskCase[];
    /** 中风险异常列表（risk_level === 'MEDIUM'） */
    medium_risk_alerts: HighRiskCase[];
    /** 所有风险异常（用于判断是否有数据） */
    all_risk_alerts: HighRiskCase[];
    /** AI 推荐的阈值配置建议 */
    threshold_recommendations: ThresholdRecommendations;
}>;

/**
 * AI 风控分析对话框主组件
 */
const AiAnalysisDialog: React.FC<Props> = ({ open, onClose, stringId }) => {
    // 存储解析后的 AI 分析数据
    const [aiAnalysisData, setAiAnalysisData] = useState<AIAnalysis | null>(null);
    // 控制局部加载状态（用于显示“加载中”占位）
    const [loading, setLoading] = useState(false);
    // 存储请求失败时的错误信息（用于 UI 提示）
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    /**
     * 当对话框打开时，自动触发数据拉取
     */
    useEffect(() => {
        if (open) {
            fetchAnalysisData();
        }
    }, [open]);

    /**
     * 从后端获取餐厅风控分析数据，并格式化为前端可用结构
     */
    const fetchAnalysisData = async () => {
        setLoading(true);
        setGlobalLoading(true); // 触发全局 loading bar
        setErrorMessage(null);
        setAiAnalysisData(null);

        try {
            // 调用 API 获取原始风控分析响应
            const res = await apiManage.get_restaurant_risk_analysis(stringId);

            // 处理业务错误（如权限不足、餐厅不存在等）
            if (res?.error) {
                const textKey = mapErrorToI18nKey(res);
                setErrorMessage(i18n.t(textKey));
                return;
            }

            // 将原始响应转换为标准化的 AIAnalysis 结构
            const newValue: AIAnalysis = {
                today_situation: {
                    total_orders: res.overview?.today?.total ?? 0,
                    // 计算今日 vs 昨日变化量
                    yesterdayCnt: (res.overview?.today?.total ?? 0) - (res.overview?.today?.yesterdayCnt ?? 0),
                    // 合并规则类和事件类异常，并过滤掉 falsy 值（如 null/undefined）
                    main_issues: [
                        ...(res.top_risks?.rule_types ?? []),
                        ...(res.top_risks?.event_types ?? []),
                    ].filter(Boolean),
                },
                trend: {
                    weekly_total: res.trend?.current_week_total ?? 0,
                    weekly_growth_rate: res.trend?.compare_last_week_percent ?? 0,
                    peak_time_ranges: res.trend?.peak_time_ranges ?? [],
                    all_type_stats: res.statistics?.all_type_stats ?? [],
                },
                ai_summary: parseOpenAIResponse({ output: res.ai_analysis }),
                high_risk_alerts: (res.high_risk_cases ?? []).filter((item) => item.risk_level === 'HIGH'),
                medium_risk_alerts: (res.high_risk_cases ?? []).filter((item) => item.risk_level === 'MEDIUM'),
                all_risk_alerts: res.high_risk_cases ?? [],
                threshold_recommendations: res.thresholdRecommendations ?? {},
            };

            setAiAnalysisData(newValue);
        } catch (err) {
            // 捕获网络或未预期错误
            console.error('Failed to fetch AI analysis:', err);
            setErrorMessage(i18n.t('common.error.network'));
        } finally {
            setLoading(false);
            setGlobalLoading(false);
        }
    };

    // ==================== 渲染辅助函数 ====================

    /**
     * 渲染“主要异常类型”文本片段
     * @returns JSX.Element 或空提示
     */
    const renderMainIssues = () => {
        const issues = aiAnalysisData?.today_situation?.main_issues || [];
        if (issues.length === 0) {
            return (
                <Typography variant="body2" color="text.secondary">
                    {i18n.t('intelligent_risk_control_page_ai_analysis_main_issues_empty')}
                </Typography>
            );
        }

        return (
            <Typography variant="body2">
                {i18n.t('intelligent_risk_control_page_ai_analysis_main_issues_prefix')}
                {issues.map((risk, idx) => (
                    <React.Fragment key={idx}>
                        <span className='funcSp1'>{risk}</span>
                        {idx < issues.length - 1 && '、'}
                    </React.Fragment>
                ))}
                {i18n.t('intelligent_risk_control_page_ai_analysis_main_issues_suffix', { count: issues.length })}
            </Typography>
        );
    };

    /**
     * 渲染“今日风控概况”卡片内容
     */
    const renderTodaySituation = () => (
        <AiAnalysisCard
            titleColor="primary"
            title={i18n.t('intelligent_risk_control_page_ai_analysis_card_today_situation')}
            content={
                <Box>
                    <Typography variant="body2">
                        {i18n.t('intelligent_risk_control_page_ai_analysis_today_triggered', {
                            count: aiAnalysisData?.today_situation?.total_orders ?? 0,
                        })}
                    </Typography>
                    <Box mt={1}>
                        {/* RenderTrend 组件负责渲染环比箭头和数值 */}
                        <RenderTrend numValue={aiAnalysisData?.today_situation?.yesterdayCnt ?? 0} />
                    </Box>
                    <Box mt={2}>{renderMainIssues()}</Box>
                </Box>
            }
        />
    );

    /**
     * 渲染“较上周”趋势标签（带颜色和符号）
     * @param rate 环比增长率（单位：%）
     * @returns 带格式化的趋势文本
     */
    const renderTrendTag = (rate: number) => {
        // 处理无变化或无效数值
        if (isNaN(rate) || rate === 0) {
            return (
                <Typography component="span">
                    {i18n.t('intelligent_risk_control_page_ai_analysis_vs_last_week_flat')}
                </Typography>
            );
        }

        const isPositive = rate > 0; // true 表示增长（风险上升）
        const displayValue = Math.abs(rate).toFixed(1); // 保留一位小数
        const color = isPositive ? 'error.light' : 'success.main'; // 风险上升用红色，下降用绿色

        return (
            <Typography component="span" sx={{ fontSize: 16 }}>
                {i18n.t('group_revenue_analysis_vs_last_week')}{' '}
                <Typography
                    component="span"
                    fontWeight="bold"
                    color={color}
                    sx={{ ml: 0.5, fontSize: 16 }}
                >
                    {isPositive ? '+' : '-'}{displayValue}%
                </Typography>
            </Typography>
        );
    };

    /**
     * 渲染“风控趋势”卡片内容
     */
    const renderTrend = () => (
        <AiAnalysisCard
            titleColor="primary"
            title={i18n.t('intelligent_risk_control_page_ai_analysis_card_trend')}
            content={
                <Box>
                    <Typography variant="body2">
                        {i18n.t('intelligent_risk_control_page_ai_analysis_weekly_total_label')}
                        <br />
                        {i18n.t('intelligent_risk_control_page_ai_analysis_trend_count', {
                            count: aiAnalysisData?.trend?.weekly_total ?? 0,
                        })}
                        （{renderTrendTag(aiAnalysisData?.trend?.weekly_growth_rate ?? 0)}）
                    </Typography>

                    <Box mt={2}>
                        <Typography variant="body2">
                            {i18n.t('intelligent_risk_control_page_ai_analysis_peak_time_label')}
                            <br />
                            {aiAnalysisData?.trend?.peak_time_ranges?.length ? (
                                aiAnalysisData.trend.peak_time_ranges.join('、')
                            ) : (
                                <Typography component="span" color="text.secondary">
                                    {i18n.t('general_text_no_data')}
                                </Typography>
                            )}
                        </Typography>
                    </Box>

                    <Box mt={2}>
                        <Typography variant="body2">
                            {i18n.t('intelligent_risk_control_page_ai_analysis_type_distribution_label')}
                        </Typography>
                        {aiAnalysisData?.trend?.all_type_stats?.length ? (
                            <>
                                {aiAnalysisData.trend.all_type_stats.map((item, idx) => (
                                    <Typography variant='body2' key={idx}>
                                        {i18n.t('intelligent_risk_control_page_ai_analysis_type_stat_line', {
                                            type: item.type,
                                            percent: item.percent,
                                        })}
                                    </Typography>
                                ))}
                            </>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                {i18n.t('intelligent_risk_control_page_ai_analysis_no_distribution_data')}
                            </Typography>
                        )}
                    </Box>
                </Box>
            }
        />
    );

    /**
     * 渲染 AI 生成的总结文本
     */
    const renderAiSummary = () => (
        <AiAnalysisCard
            titleColor="primary"
            title={i18n.t('intelligent_risk_control_page_ai_analysis_card_ai_summary')}
            content={
                aiAnalysisData?.ai_summary ? (
                    // whiteSpace="pre-line" 保留换行符，使 AI 输出更易读
                    <Typography variant="body2" whiteSpace="pre-line">
                        {aiAnalysisData.ai_summary}
                    </Typography>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        {i18n.t('intelligent_risk_control_page_ai_analysis_no_ai_result')}
                    </Typography>
                )
            }
        />
    );

    /**
     * 渲染高/中风险告警列表（含分隔）
     * @returns 风险项 JSX 列表
     */
    const renderRiskAlerts = () => {
        const highRisks = aiAnalysisData?.high_risk_alerts || [];
        const mediumRisks = aiAnalysisData?.medium_risk_alerts || [];

        if (highRisks.length === 0 && mediumRisks.length === 0) {
            return (
                <Typography color="text.secondary">
                    {i18n.t('intelligent_risk_control_page_ai_analysis_no_risk_alerts')}
                </Typography>
            );
        }

        return (
            <Box display="flex" flexDirection="column" gap={3}>
                {/* 高风险项 */}
                {highRisks.map((item, idx) => (
                    <Box key={`high-${idx}`}>
                        <Typography variant="body2" >{item.summary}</Typography>
                        <Typography variant="body2" mt={0.5}>{item.description}</Typography>
                        <Typography variant="body2" mt={0.5}>{item.suggest_action}</Typography>
                    </Box>
                ))}

                {/* 中风险项（如有） */}
                {mediumRisks.length > 0 && (
                    <>
                        <Typography variant="body1" color='error.main'>
                            {i18n.t('intelligent_risk_control_page_ai_analysis_medium_risk_title')}
                        </Typography>
                        {mediumRisks.map((item, idx) => (
                            <Box key={`med-${idx}`} mt={1}>
                                <Typography variant="body2">{item.summary}</Typography>
                                <Typography variant="body2" mt={0.5}>{item.description}</Typography>
                                <Typography variant="body2" mt={0.5}>{item.suggest_action}</Typography>
                            </Box>
                        ))}
                    </>
                )}
            </Box>
        );
    };

    /**
     * 渲染高风险告警卡片（包含中风险）
     */
    const renderHighRiskAlerts = () => (
        <AiAnalysisCard
            title={i18n.t('intelligent_risk_control_page_ai_analysis_card_high_risk')}
            titleColor="error"
            content={renderRiskAlerts()}
        />
    );

    /**
     * 渲染阈值设置建议内容
     */
    const renderRecommendations = () => {
        const rec = aiAnalysisData?.threshold_recommendations;
        // 若推荐对象为空或无有效字段，则显示空提示
        if (!rec || (!rec.title && !rec.readable?.length && !rec.description)) {
            return (
                <Typography color="text.secondary">
                    {i18n.t('intelligent_risk_control_page_ai_analysis_no_threshold_recommendations')}
                </Typography>
            );
        }

        return (
            <Box>
                {rec.title && <Typography variant="body2" >{rec.title}</Typography>}
                {rec.readable?.map((line, idx) => (
                    <Typography key={idx} variant="body2" mt={0.5}>{line}</Typography>
                ))}
                {rec.description && <Typography variant="body2" mt={1}>{rec.description}</Typography>}
            </Box>
        );
    };

    /** 弹窗标题文案 */
    const dialogTitle = i18n.t('intelligent_risk_control_page_dialog_title_ai_analysis');

    // ==================== 主渲染逻辑 ====================

    // 加载态：显示简单占位
    if (loading) {
        return (
            <DialogComponent
                open={open}
                onClose={onClose}
                defaultHeaderProps={{ title: dialogTitle, titleAlign: 'center', titleColor: 'error' }}
                maxWidth="md"
                heightSize="large"
            >
                <CustomerLoading />
            </DialogComponent>
        );
    }

    return (
        <DialogComponent
            open={open}
            onClose={onClose}
            defaultHeaderProps={{ title: dialogTitle, titleAlign: 'center', titleColor: 'error' }}
            maxWidth="md"
            heightSize="large"
        >
            {/* 错误状态：显示错误提示 */}
            {errorMessage ? (
                <Alert severity="error" sx={{ m: 2 }}>{errorMessage}</Alert>
            ) : aiAnalysisData ? (
                <Box
                    display="grid"
                    gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' }}
                    gap={3}
                >
                    {/* 左栏：今日概况 + AI 总结 */}
                    <Box display="flex" flexDirection="column" gap={3}>
                        {renderTodaySituation()}
                        {renderAiSummary()}
                    </Box>

                    {/* 中栏：趋势 + 阈值建议 */}
                    <Box display="flex" flexDirection="column" gap={3}>
                        {renderTrend()}
                        <AiAnalysisCard
                            title={i18n.t('intelligent_risk_control_page_ai_analysis_card_threshold_recommendations')}
                            titleColor="primary"
                            content={renderRecommendations()}
                        />
                    </Box>

                    {/* 右栏：风险告警详情 */}
                    <Box>{renderHighRiskAlerts()}</Box>
                </Box>
            ) : (
                < Paper sx={{ borderRadius: '16px', p: 3, textAlign: 'center', minHeight: 400 }}>
                    <Typography variant="body2" color="text.secondary">
                        {i18n.t('general_text_no_data')}
                    </Typography>
                </Paper>
            )
            }
        </DialogComponent >
    );
};

export default AiAnalysisDialog;